import type { ResultColumn, VizType } from "@cd/core";

export interface VizShape {
  dimensions: Pick<ResultColumn, "type" | "grain">[];
  metrics: number;
  /** True when some metric can't be summed (averages, distinct counts). Unknown until the query runs. */
  nonAdditive?: boolean;
}

export interface VizSpec {
  type: VizType;
  label: string;
  description: string;
  /** Returns a user-facing reason when this viz can't show the current selection. */
  check(shape: VizShape): string | null;
}

const isTime = (d: Pick<ResultColumn, "type">) => d.type === "time";

export const VIZ_SPECS: VizSpec[] = [
  {
    type: "kpi", label: "Number", description: "A single headline number with an optional comparison.",
    check: (s) => (s.dimensions.length ? "Numbers show a single total — remove groupings." : null),
  },
  {
    type: "line", label: "Line", description: "Trends over time.",
    check: (s) =>
      !s.dimensions[0] || !isTime(s.dimensions[0])
        ? "Line charts need a date grouping first."
        : s.dimensions.length > 2 ? "Line charts support a date plus at most one series grouping." : null,
  },
  {
    type: "area", label: "Area", description: "Trends over time, emphasising volume.",
    check: (s) =>
      !s.dimensions[0] || !isTime(s.dimensions[0])
        ? "Area charts need a date grouping first."
        : s.dimensions.length > 2 ? "Area charts support a date plus at most one series grouping." : null,
  },
  {
    type: "bar", label: "Bar", description: "Compare categories.",
    check: (s) =>
      !s.dimensions.length ? "Bar charts need at least one grouping." : s.dimensions.length > 2 ? "Bar charts support at most two groupings." : null,
  },
  {
    type: "combo", label: "Combo", description: "Bars and lines together, e.g. sales with order count.",
    check: (s) =>
      s.dimensions.length !== 1
        ? "Combo charts need exactly one grouping, usually a date."
        : s.metrics < 2 ? "Combo charts need at least two metrics: the first as bars, the rest as lines." : null,
  },
  {
    type: "pie", label: "Donut", description: "Share of a total across a few categories.",
    check: (s) =>
      s.dimensions.length !== 1 || isTime(s.dimensions[0]!)
        ? "Donut charts need exactly one non-date grouping."
        : s.metrics !== 1 ? "Donut charts show exactly one metric." : null,
  },
  {
    type: "heatmap", label: "Heatmap", description: "One metric across two groupings, e.g. weekday by hour.",
    check: (s) =>
      s.dimensions.length !== 2 ? "Heatmaps need exactly two groupings." : s.metrics !== 1 ? "Heatmaps show exactly one metric." : null,
  },
  {
    type: "scatter", label: "Scatter", description: "Two metrics against each other, one point per value.",
    check: (s) =>
      !s.dimensions.length
        ? "Scatter plots need a grouping — each point is one of its values."
        : s.dimensions.length > 2 ? "Scatter plots support a point grouping plus at most one color grouping."
        : s.metrics < 2 || s.metrics > 3 ? "Scatter plots need two metrics (across and up), plus an optional third for point size." : null,
  },
  {
    type: "treemap", label: "Treemap", description: "Share of a total across many categories, optionally nested.",
    check: (s) =>
      !s.dimensions.length || s.dimensions.length > 2
        ? "Treemaps need one or two groupings."
        : s.metrics !== 1 ? "Treemaps show exactly one metric."
        : s.nonAdditive ? "Treemaps show parts of a total, so the metric must add up — not an average, ratio or distinct count." : null,
  },
  {
    type: "funnel", label: "Funnel", description: "Successive stages of one group, e.g. booked → seated, with the drop-off between them.",
    check: (s) =>
      s.dimensions.length ? "Funnels show each metric as a stage — remove groupings." : s.metrics < 2 ? "Funnels need at least two metrics, one per stage, in order." : null,
  },
  { type: "table", label: "Table", description: "Detailed rows.", check: () => null },
  {
    type: "pivot", label: "Pivot", description: "Cross-tab: rows by columns.",
    check: (s) => (s.dimensions.length < 2 ? "Pivot tables need at least two groupings." : null),
  },
];

export const vizSpec = (t: VizType) => VIZ_SPECS.find((v) => v.type === t)!;

/** Best default viz for a selection. */
export function suggestViz(shape: VizShape): VizType {
  if (!shape.dimensions.length) return "kpi";
  // Weekdays are categories to compare, not a trend.
  if (isTime(shape.dimensions[0]!) && shape.dimensions[0]!.grain !== "day_of_week" && shape.dimensions.length <= 2) return "line";
  if (shape.dimensions.length <= 2) return "bar";
  return "table";
}
